import type { Map as MapboxMap } from 'mapbox-gl';
import { type RefObject, useEffect } from 'react';
import type { IHighestPerformingCompetitor } from '@/types/api';
import {
  competitorMatchesStationFilters,
  type StationVisibilityState,
} from '../utils/applyLayerVisibility';
import { MapLayersService } from '../utils/MapLayersService';

export interface UseHighestPerformingCompetitorMarkOptions {
  map: RefObject<MapboxMap | null>;
  /** The style has loaded and the crown sprite is registered. */
  enabled: boolean;
  /** Winning station for the open location. Absent when none was stored. */
  competitor: IHighestPerformingCompetitor | null | undefined;
  /** Competitor tiles are on. The mark is skipped while they are off. */
  tilesEnabled: boolean;
  /** Charge-range, Electra, and low-HPC toggles that hide station pins. */
  stationFilters: StationVisibilityState;
}

/**
 * Crowns the winning competitor pin while that location is open
 * and the pin itself would be drawn. The cleanup drops the mark this card drew.
 * A card that is not showing one leaves the source alone: the deal card and the
 * workspace card share it, and only the card that drew the mark removes it.
 */
export function useHighestPerformingCompetitorMark({
  map,
  enabled,
  competitor,
  tilesEnabled,
  stationFilters,
}: UseHighestPerformingCompetitorMarkOptions) {
  const longitude = competitor?.longitude;
  const latitude = competitor?.latitude;
  const performance = competitor?.performance;
  const visible =
    tilesEnabled &&
    competitor != null &&
    competitorMatchesStationFilters(competitor, stationFilters);

  useEffect(() => {
    if (!map.current || !enabled) return;
    if (!visible || longitude == null || latitude == null || performance == null) return;

    const service = new MapLayersService(map.current);
    service.showHighestPerformingCompetitor(longitude, latitude, performance);

    return () => {
      if (!map.current) return;
      new MapLayersService(map.current).clearHighestPerformingCompetitor();
    };
  }, [map, enabled, visible, longitude, latitude, performance]);
}
